import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Receipt, Plus, Camera, Eye, Trash, Sparkle, CheckCircle, Clock, CurrencyDollar, ImageSquare, FilePdf,
  CaretDown, CaretRight, MagnifyingGlass, X, UploadSimple, WarningCircle, ChatCircleDots, Bank, PencilSimple,
  ArrowsClockwise, ArrowCounterClockwise,
} from "@phosphor-icons/react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import DocumentViewer from "../components/DocumentViewer";
import PageHelpButton from "../components/PageHelpButton";
import QboMatchPanel from "../components/QboMatchPanel";
import BankStatementUploadDialog from "../components/BankStatementUploadDialog";
import EntityHistory from "../components/EntityHistory";
import FlowStrip from "../components/FlowStrip";
import OwnerBadge, { ownerLeftStripe } from "../components/OwnerBadge";
import OwnerFilter from "../components/OwnerFilter";
import DirectedToPicker from "../components/DirectedToPicker";
import LifecycleStepper from "../components/LifecycleStepper";
import { nextActorForRemittance, ownBucketFor } from "../lib/ownership";

// Master switch: when the QBO matching engine is disabled (the default — see
// QBO_MATCHING_ENABLED on the backend), all QBO-related UI on this page is
// hidden so the page becomes a pure "uploaded remittance advices" list that
// the bookkeeper can grab. Flip to `true` to re-show matching UI.
const QBO_ENABLED = false;

// Module-level static so the array isn't reallocated on every render.
const REMITTANCE_FLOW_STEPS = [
  { icon: Camera,         title: "Upload",         sub: "Photo, PDF, or paste a screenshot.",            accent: "#002FA7" },
  { icon: Sparkle,        title: "Auto-fill",      sub: "Payer, amount, date, invoice # — read for you.", accent: "#7C3AED" },
  { icon: CheckCircle,    title: "On file",        sub: "Bookkeeper grabs it from here.",                 accent: "#10B981" },
];

// Full QBO matching journey — kept around for when matching is re-enabled.
// eslint-disable-next-line no-unused-vars
const REMITTANCE_FLOW_STEPS_QBO = [
  { icon: Camera,         title: "Upload",         sub: "Photo, PDF, or paste a screenshot.",            accent: "#002FA7" },
  { icon: Sparkle,        title: "Auto-fill",      sub: "Payer, amount, date, invoice # — read for you.", accent: "#7C3AED" },
  { icon: MagnifyingGlass,title: "Find matches",   sub: "Pick the QBO invoice(s) — split if needed.",     accent: "#0EA5E9" },
  { icon: CurrencyDollar, title: "Apply payment",  sub: "One click posts a Payment in QuickBooks.",       accent: "#2CA01C" },
  { icon: UploadSimple,   title: "Bank statement", sub: "Drop CSV/Excel/PDF — deposits cleared.",         accent: "#F59E0B" },
  { icon: CheckCircle,    title: "Cleared",        sub: "Row turns green. Books are caught up.",          accent: "#10B981" },
];

export default function PaymentAdvices() {
  const { activeCompanyId, activeCompany } = useCompany();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [queue, setQueue] = useState([]); // [{id, name, status: 'uploading'|'done'|'error'}]
  const [viewerStartId, setViewerStartId] = useState(null);
  const fileRef = useRef(null);
  const libraryRef = useRef(null);
  const [editing, setEditing] = useState({});

  const [qboConnected, setQboConnected] = useState(false);
  const [qboCompanyName, setQboCompanyName] = useState(null);
  // Set of "YYYY-MM" months that are closed for this company. Used to
  // suppress the Overdue badge on advices whose period is already locked
  // (nothing actionable about reconciling a closed month).
  const [closedMonths, setClosedMonths] = useState(new Set());
  const [filter, setFilter] = useState("all"); // "all" | "pending" | "deposited" | "applied" | "cleared" | "needs_review"
  const [ownerFilter, setOwnerFilter] = useState("all"); // "all" | "staff" | "bookkeeper" | "owner" | "done" — never persisted
  const [search, setSearch] = useState("");
  const [expandedIds, setExpandedIds] = useState({});
  const [bankStmtOpen, setBankStmtOpen] = useState(false);
  const [recreateAdvice, setRecreateAdvice] = useState(null);
  // Direct-to-bank workflow toggles. When `show_advanced_bank_tools` is OFF
  // (the default), we hide the bank-statement upload, "Sync bank clearance",
  // and "Re-create QBO Deposit" controls — Tessina sees a simpler page where
  // clearance is confirmed manually via the per-row "Mark as cleared" button.
  const showAdvancedBank = !!activeCompany?.show_advanced_bank_tools;
  const defaultDepositName = activeCompany?.default_deposit_account_name || null;
  // "Ask about this remittance" dialog state — pre-fills the inquiry with this remittance's
  // payer/amount/date so it lands in the Inquiries page already linked to the right doc.
  const [askAdvice, setAskAdvice] = useState(null);
  const [askTitle, setAskTitle] = useState("");
  const [askBody, setAskBody] = useState("");
  const [askSubmitting, setAskSubmitting] = useState(false);
  const defaultDirectedTo =
    user?.role === "owner" ? "bookkeeper" :
    user?.role === "bookkeeper" ? "owner" :
    "bookkeeper";
  const [askDirectedTo, setAskDirectedTo] = useState(defaultDirectedTo);
  const toggleExpand = (id) => setExpandedIds((m) => ({ ...m, [id]: !m[id] }));

  const load = useCallback(async () => {
    if (!activeCompanyId) return;
    const { data } = await api.get(`/payment-advices?company_id=${activeCompanyId}`);
    setItems(data);
  }, [activeCompanyId]);

  const loadQboStatus = useCallback(async () => {
    if (!activeCompanyId) return;
    try {
      const { data } = await api.get(`/qbo/status?company_id=${activeCompanyId}`);
      setQboConnected(!!data.connected);
      setQboCompanyName(data.qbo_company_name || null);
    } catch (e) {
      console.warn("Failed to load QBO status:", e);
      setQboConnected(false);
      setQboCompanyName(null);
    }
  }, [activeCompanyId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadQboStatus(); }, [loadQboStatus]);

  // ---- Auto-sweep (UF → bank) ----
  // Runs silently once per session per company when QBO is connected and we
  // know which bank account to deposit into. The bank id is remembered in
  // localStorage from the user's last interaction with the bank-statement dialog.
  const [sweepResult, setSweepResult] = useState(null); // { auto_cleared, total_amount_cleared, bank_account_name } | null
  const [sweepDismissed, setSweepDismissed] = useState(false);
  useEffect(() => {
    if (!qboConnected || !activeCompanyId) return;
    const bankId = localStorage.getItem(`autoSweep:bankId:${activeCompanyId}`);
    if (!bankId) return; // No bank picked yet — user hasn't run the manual upload flow
    const key = `autoSwept:${activeCompanyId}:${new Date().toISOString().slice(0, 10)}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    (async () => {
      try {
        const { data } = await api.post(`/qbo/auto-sweep`, null, {
          params: { company_id: activeCompanyId, bank_account_id: bankId, look_back_days: 90 },
        });
        if (data?.auto_cleared > 0) {
          setSweepResult(data);
          await load();
        }
      } catch (err) {
        // Auto-sweep is best-effort. Bank-account changes or QBO hiccups can
        // return 4xx — user can always use the manual statement dialog. We log
        // to console so DevTools can surface unexpected failures, but never
        // surface a toast (the auto-sweep is invisible by design).
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.debug("auto-sweep skipped:", err?.response?.status, err?.response?.data?.detail || err?.message);
        }
      }
    })();
  }, [qboConnected, activeCompanyId, load]);

  // Fetch the list of closed months so we can suppress Overdue badges on
  // historical advices. Closed = settled, nothing actionable to chase.
  useEffect(() => {
    if (!activeCompanyId) {
      setClosedMonths(new Set());
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/close/closed-months`, {
          params: { company_id: activeCompanyId },
        });
        setClosedMonths(new Set(data?.months || []));
      } catch (e) {
        // Non-fatal — worst case we show Overdue badges in closed months.
        setClosedMonths(new Set());
      }
    })();
  }, [activeCompanyId]);

  // Paste screenshots (Ctrl+V / Cmd+V) — perfect for desktop remittance emails
  useEffect(() => {
    const handlePaste = async (e) => {
      if (!activeCompanyId) return;
      const items = e.clipboardData?.items;
      if (!items) return;
      const files = [];
      for (const item of items) {
        if (item.kind === "file") {
          const file = item.getAsFile();
          if (file) files.push(file);
        }
      }
      if (files.length) {
        e.preventDefault();
        await handleUpload(files);
      }
    };
    window.addEventListener("paste", handlePaste);
    return () => window.removeEventListener("paste", handlePaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCompanyId]);

  const uploadOne = async (file) => {
    const qid = `q-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setQueue((q) => [...q, { id: qid, name: file.name, status: "uploading" }]);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("company_id", activeCompanyId);
    try {
      const { data } = await api.post("/payment-advices/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const splitCount = Number(data?.split_count) > 1 ? Number(data.split_count) : 0;
      const label = splitCount
        ? `Found ${splitCount} advices in PDF`
        : (typeof data.payer_name === "string" ? data.payer_name : null);
      setQueue((q) => q.map((x) => (x.id === qid ? { ...x, status: "done", extracted: label } : x)));
      return { data, advicesCreated: splitCount || 1 };
    } catch (e) {
      setQueue((q) => q.map((x) => (x.id === qid ? { ...x, status: "error" } : x)));
      return null;
    }
  };

  const handleUpload = async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;
    // Process in parallel with a cap of 3 concurrent for API safety
    const MAX_CONCURRENT = 3;
    let index = 0;
    let totalAdvicesCreated = 0;
    const runNext = async () => {
      if (index >= files.length) return;
      const file = files[index++];
      const result = await uploadOne(file);
      if (result?.advicesCreated) totalAdvicesCreated += result.advicesCreated;
      await runNext();
    };
    await Promise.all(Array.from({ length: Math.min(MAX_CONCURRENT, files.length) }, runNext));
    await load();
    // If a multi-advice PDF was split, callers care about the row count, not file count.
    if (totalAdvicesCreated > files.length) {
      toast.success(`Created ${totalAdvicesCreated} advices from ${files.length} file${files.length === 1 ? "" : "s"}`);
    } else {
      toast.success(`Processed ${files.length} record${files.length === 1 ? "" : "s"}`);
    }
    setTimeout(() => setQueue((q) => q.filter((x) => x.status === "uploading")), 3000);
  };

  const handleSave = async (id) => {
    const patch = editing[id];
    if (!patch) return;
    try {
      await api.patch(`/payment-advices/${id}`, {
        ...patch,
        amount: patch.amount !== undefined && patch.amount !== "" ? parseFloat(patch.amount) : null,
      });
      toast.success("Saved");
      setEditing((e) => ({ ...e, [id]: undefined }));
      await load();
    } catch (e) {
      toast.error("Failed to save");
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Delete this payment advice?")) return;
    try {
      await api.delete(`/payment-advices/${id}`);
      await load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const handleMoveToFiles = async (a) => {
    const label = a.payer_name ? ` (${a.payer_name})` : "";
    if (!confirm(
      `Move this advice${label} to the Files page?\n\n` +
      `The file stays available on Files for reference, but the remittance row is removed — ` +
      `useful when the invoice is already paid in QBO and the row will never clear.`
    )) return;
    try {
      await api.post(`/payment-advices/${a.id}/move-to-files`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to move to Files");
    }
  };

  const handleClearReview = async (a) => {
    if (!confirm(`Clear the bookkeeper-review flag on this remittance${a.payer_name ? ` (${a.payer_name})` : ""}? It won't be re-flagged automatically.`)) return;
    try {
      await api.post(`/qbo/clear-review/${a.id}`);
      toast.success("Review flag cleared");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to clear review flag");
    }
  };

  // --- Ask about this remittance --------------------------------------------------
  // Builds a friendly subject identifying the document so the inquiry shows up on
  // the Inquiries page already linked to the right remittance.
  const adviceLabel = (a) => {
    const payer = a?.payer_name || "Unknown payer";
    const amt = a?.amount != null ? `$${Number(a.amount).toFixed(2)}` : "";
    const date = a?.date ? ` on ${a.date}` : "";
    return `${payer}${amt ? " — " + amt : ""}${date}`;
  };
  const openAskDialog = (a) => {
    setAskAdvice(a);
    setAskTitle(`Re: Remittance — ${adviceLabel(a)}`);
    setAskBody("");
    setAskDirectedTo(defaultDirectedTo);
  };
  const submitAsk = async () => {
    if (!askAdvice || !askTitle.trim()) return;
    setAskSubmitting(true);
    try {
      const ctxBits = [
        `Regarding remittance from ${askAdvice.payer_name || "(no payer)"}`,
        askAdvice.amount != null ? `$${Number(askAdvice.amount).toFixed(2)}` : null,
        askAdvice.date ? `dated ${askAdvice.date}` : null,
        askAdvice.invoice_number ? `invoice ${askAdvice.invoice_number}` : null,
      ].filter(Boolean).join(" · ");
      const fullBody = askBody.trim()
        ? `${ctxBits}\n\n${askBody.trim()}`
        : ctxBits;
      await api.post("/questions", {
        company_id: activeCompanyId,
        title: askTitle.trim(),
        body: fullBody,
        due_date: null,
        linked_document_id: askAdvice.document_id || null,
        directed_to: askDirectedTo || null,
      });
      toast.success("Inquiry submitted");
      setAskAdvice(null);
      setAskTitle("");
      setAskBody("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit inquiry");
    }
    setAskSubmitting(false);
  };
  // --------------------------------------------------------------------------------

  const handleDownload = async () => {
    // legacy — no longer used; we use the viewer now
  };

  // Build a list of viewable docs (only those with a document_id) for the viewer's prev/next nav
  const viewerDocs = items
    .filter((a) => !!a.document_id)
    .map((a) => ({
      id: a.document_id,
      content_type: a.document_content_type,
      display_filename: `${a.payer_name || "Remittance"} - $${a.amount || "0"}`,
      original_filename: `advice-${a.id}`,
    }));

  const beginEdit = (item) => {
    setEditing((e) => ({
      ...e,
      [item.id]: {
        payer_name: item.payer_name || "",
        amount: item.amount ?? "",
        date: item.date || "",
        invoice_number: item.invoice_number || "",
      },
    }));
  };

  const [reExtracting, setReExtracting] = useState({});
  const handleReExtract = async (id) => {
    if (!confirm("Re-run AI on this row? This will overwrite payer / amount / date / invoice from the original document.")) return;
    setReExtracting((m) => ({ ...m, [id]: true }));
    try {
      await api.post(`/payment-advices/${id}/re-extract`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Re-run failed");
    } finally {
      setReExtracting((m) => ({ ...m, [id]: false }));
    }
  };

  const statusTone = {
    pending: { bg: "bg-[#F59E0B]/10", text: "text-[#F59E0B]", dot: "bg-[#F59E0B]" },
    deposited: { bg: "bg-[#002FA7]/10", text: "text-[#002FA7]", dot: "bg-[#002FA7]" },
    matched: { bg: "bg-[#10B981]/10", text: "text-[#10B981]", dot: "bg-[#10B981]" },
    applied: { bg: "bg-[#2CA01C]/10", text: "text-[#2CA01C]", dot: "bg-[#2CA01C]" },
  };

  // Single canonical bucket per advice — mutually exclusive, so an advice with
  // `status=cleared` AND `review_status=needs_review` only appears in "Needs review".
  // Once she clears that flag, the advice falls through to its real lifecycle bucket.
  // Priority order (top wins):
  //   needs_review → pending → applied → cleared → deposited
  const primaryBucket = (a) => {
    if (a.status === "split") return "split";  // hidden everywhere
    if (a.review_status === "needs_review") return "needs_review";
    if (a.status === "pending") return "pending";
    // Deposited = QBO Deposit transaction recorded (or legacy status=deposited)
    if (a.qbo_deposit_id || a.status === "deposited") return "deposited";
    if (a.qbo_cleared_at) return "cleared";
    // Applied to QBO as Payment but bank hasn't cleared it yet
    if (a.status === "applied" || a.status === "matched") return "applied";
    return "pending";  // fallback
  };

  const counts = useMemo(() => {
    const c = { all: 0, pending: 0, deposited: 0, applied: 0, cleared: 0, needs_review: 0, stuck: 0, in_undeposited: 0 };
    const now = Date.now();
    items.forEach((a) => {
      const bucket = primaryBucket(a);
      if (bucket === "split") return;
      c.all += 1;
      c[bucket] += 1;
      // Stuck: applied 7+ days ago and still not cleared (separate signal, not a tab)
      if (bucket === "applied" && a.applied_at) {
        const ageDays = (now - new Date(a.applied_at).getTime()) / (1000 * 60 * 60 * 24);
        if (ageDays >= 7) c.stuck += 1;
      }
      // In Undeposited Funds: Payment posted to QBO but no Deposit transaction recorded
      // — applies to both "applied" (no clearance yet) and "cleared" (cleared but deposit
      // never posted) buckets. 3-day window because that's typical bank-feed cadence.
      if (a.qbo_payment_id && !a.qbo_deposit_id && a.applied_at) {
        const ageDays = (now - new Date(a.applied_at).getTime()) / (1000 * 60 * 60 * 24);
        if (ageDays >= 3) c.in_undeposited += 1;
      }
    });
    return c;
  }, [items]);

  // Resolved list of advices currently parked in QBO Undeposited Funds (same
  // criteria as counts.in_undeposited). Surfaced inline in the banner so the
  // user can see WHICH payments need a Deposit posted, instead of just the
  // total count.
  const inUndepositedItems = useMemo(() => {
    const now = Date.now();
    return items.filter((a) => {
      if (!a.qbo_payment_id || a.qbo_deposit_id || !a.applied_at) return false;
      const ageDays = (now - new Date(a.applied_at).getTime()) / (1000 * 60 * 60 * 24);
      return ageDays >= 3;
    });
  }, [items]);
  const [showInUndepositedList, setShowInUndepositedList] = useState(false);

  // Resolved list for the "applied 7+ days, not yet cleared" banner —
  // same expand-and-jump pattern as the Undeposited Funds list.
  const stuckItems = useMemo(() => {
    const now = Date.now();
    return items.filter((a) => {
      if (primaryBucket(a) !== "applied" || !a.applied_at) return false;
      const ageDays = (now - new Date(a.applied_at).getTime()) / (1000 * 60 * 60 * 24);
      return ageDays >= 7;
    });
  }, [items]);
  const [showStuckList, setShowStuckList] = useState(false);

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((a) => {
      const bucket = primaryBucket(a);
      if (bucket === "split") return false;
      // Tab filter — mutually exclusive based on primaryBucket
      if (filter === "pending" && bucket !== "pending") return false;
      if (filter === "applied" && bucket !== "applied") return false;
      if (filter === "cleared" && bucket !== "cleared") return false;
      if (filter === "deposited" && bucket !== "deposited") return false;
      if (filter === "needs_review" && bucket !== "needs_review") return false;
      // "all" shows active work only — hide cleared and deposited so the
      // bookkeeper isn't drowned in finished items. She can still see them by
      // explicitly tapping the Cleared or Deposited tabs.
      if (filter === "all" && (bucket === "cleared" || bucket === "deposited")) return false;
      // Ownership filter (page-local, defaults to "all")
      if (ownerFilter !== "all") {
        const actor = nextActorForRemittance(a);
        if (ownerFilter === "done") {
          if (actor !== null) return false;
        } else if (actor !== ownerFilter) {
          return false;
        }
      }
      // Filter by search
      if (q) {
        const hay = [
          a.payer_name || "",
          String(a.amount || ""),
          a.invoice_number || "",
          a.date || "",
        ].join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [items, filter, search, ownerFilter]);

  // Counts per ownership bucket — respects the active status filter so the
  // numbers always match what's actually visible. (Previously these were
  // computed across ALL items, which led to "DONE 4" showing nothing visible
  // when the status filter excluded already-done rows.)
  const ownCounts = useMemo(() => {
    const c = { all: 0, staff: 0, bookkeeper: 0, owner: 0, done: 0 };
    items.forEach((a) => {
      if (a.status === "split") return; // hidden everywhere
      const bucket = primaryBucket(a);
      if (filter === "pending" && bucket !== "pending") return;
      if (filter === "applied" && bucket !== "applied") return;
      if (filter === "cleared" && bucket !== "cleared") return;
      if (filter === "deposited" && bucket !== "deposited") return;
      if (filter === "needs_review" && bucket !== "needs_review") return;
      // "all" passes through
      c.all += 1;
      const actor = nextActorForRemittance(a);
      if (actor === "staff") c.staff += 1;
      else if (actor === "bookkeeper") c.bookkeeper += 1;
      else if (actor === "owner") c.owner += 1;
      else c.done += 1;
    });
    return c;
  }, [items, filter]);

  // Mutually-exclusive primary buckets: needs_review wins over lifecycle status,
  // so an advice can only appear in one tab at a time. "Active" hides Cleared
  // and Deposited so the bookkeeper sees only items still needing attention.
  // The Cleared / Deposited tabs are still available to look back.
  const activeCount = counts.all - counts.cleared - counts.deposited;
  const filterChips = QBO_ENABLED
    ? [
        { v: "all", label: "Active", count: activeCount },
        { v: "pending", label: "Pending", count: counts.pending },
        { v: "applied", label: "Applied", count: counts.applied },
        { v: "cleared", label: "Cleared", count: counts.cleared },
        { v: "deposited", label: "Deposited", count: counts.deposited },
        { v: "needs_review", label: "Needs review", count: counts.needs_review },
      ]
    : [
        // Simplified: without QBO matching there are no lifecycle states.
        // Every uploaded advice is just "uploaded". Single chip = no chips.
        { v: "all", label: "All", count: counts.all },
      ];

  // Detect rows that have a stale review flag — already moved past upload but
  // still wearing "Bookkeeper review". Owner+bookkeeper can backfill in one shot.
  const staleFlagCount = useMemo(
    () => items.filter((a) =>
      a.review_status === "needs_review" &&
      (a.qbo_payment_id || a.qbo_cleared_at || a.qbo_deposit_id)
    ).length,
    [items]
  );

  // Count of "stuck Overdue" rows — applied 7+ days ago, not cleared, not in
  // a closed period. Used to surface the one-shot historical-cleanup button.
  const stuckOverdueCount = useMemo(
    () => items.filter((a) => {
      const isCleared = !!a.qbo_cleared_at;
      const isApplied = a.status === "applied" || a.status === "matched";
      if (!isApplied || isCleared || !a.applied_at) return false;
      const adviceMonth = (a.date || a.applied_at || "").slice(0, 7);
      if (adviceMonth && closedMonths.has(adviceMonth)) return false;
      const ageDays = (Date.now() - new Date(a.applied_at).getTime()) / (1000 * 60 * 60 * 24);
      return ageDays >= 7;
    }).length,
    [items, closedMonths],
  );

  // One-shot historical cleanup — marks every applied-uncleared advice older
  // than today (default) as cleared. Use case: portal was adopted mid-year,
  // QBO has been reconciling these all along, but the portal sees them as
  // "applied but uncleared" forever and flags them Overdue.
  // "Recover missing" — find payment-advice documents that ended up on Files
  // but never created a remittance row (orphans from pre-fix uploads where
  // the server died between document-save and advice-save). Safe to run
  // anytime — non-destructive scan + add-missing-rows.
  const handleRecoverOrphans = async () => {
    if (!activeCompanyId) return;
    if (!window.confirm(
      "Look for payment-advice photos that ended up on Files but didn't create " +
      "a remittance row? This is safe and non-destructive."
    )) return;
    try {
      const fd = new FormData();
      fd.append("company_id", activeCompanyId);
      const { data } = await api.post("/payment-advices/recover-orphans", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const n = data?.recovered || 0;
      if (n > 0) {
        toast.success(`Recovered ${n} missing remittance${n === 1 ? "" : "s"}`, { duration: 6000 });
      } else {
        toast.success("No orphans found — everything is in sync.");
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Recovery failed");
    }
  };


  const handleBulkMarkCleared = async () => {
    if (!activeCompanyId) return;
    const today = new Date().toISOString().slice(0, 10);
    const input = prompt(
      `Mark ALL applied-but-uncleared remittances as cleared (in the portal).\n\n` +
      `Choose a cutoff date — anything applied STRICTLY BEFORE this date will be marked cleared.\n` +
      `Today: ${today}\n\n` +
      `This is a portal-only flag (does NOT push to QuickBooks). Use this once when you ` +
      `first start using the bank-statement auto-clear pipeline.`,
      today,
    );
    if (!input) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(input.trim())) {
      toast.error("Date must be YYYY-MM-DD");
      return;
    }
    try {
      const { data } = await api.post(`/payment-advices/bulk-mark-cleared`, {
        company_id: activeCompanyId,
        applied_before: input.trim(),
      });
      const n = data?.cleared || 0;
      if (n > 0) {
        toast.success(`Cleaned up ${n} historical remittance${n === 1 ? "" : "s"}`, { duration: 6000 });
      } else {
        toast.success("Nothing to clean — no historical overdue rows matched.");
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cleanup failed");
    }
  };

  // One-shot auto-fire: the first time the page loads AND we detect stale flags
  // AND the user has permission, silently run the backfill. The flags are
  // semantically meaningless by definition (lifecycle has already progressed
  // past the gate they were guarding), so healing them is risk-free.
  // Per-company ref so switching companies re-evaluates.
  const autoBackfillFiredCompaniesRef = useRef(new Set());
  useEffect(() => {
    if (!activeCompanyId || items.length === 0) return;
    if (autoBackfillFiredCompaniesRef.current.has(activeCompanyId)) return;
    if (staleFlagCount === 0) return;
    if (user?.role !== "owner" && user?.role !== "bookkeeper") return;
    autoBackfillFiredCompaniesRef.current.add(activeCompanyId);
    (async () => {
      try {
        const r = await api.post(`/qbo/backfill-review-flags?company_id=${activeCompanyId}`);
        const fixed = r.data?.fixed ?? 0;
        if (fixed > 0) {
          toast.success(`Auto-cleared ${fixed} stale review flag${fixed === 1 ? "" : "s"}`, { duration: 4000 });
          await load();
        }
      } catch (e) {
        // Silent — fall back to manual button if it fails
        // eslint-disable-next-line no-console
        console.warn("Auto-backfill failed:", e);
      }
    })();
  }, [activeCompanyId, items.length, staleFlagCount, user?.role]);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleBackfillFlags = async () => {
    if (!activeCompanyId) return;
    if (!confirm(`Auto-clear the bookkeeper-review flag on ${staleFlagCount} remittance(s) that have already been applied/cleared/deposited? Their lifecycle is already complete.`)) return;
    try {
      const r = await api.post(`/qbo/backfill-review-flags?company_id=${activeCompanyId}`);
      toast.success(`Cleared ${r.data?.fixed ?? 0} stale review flag(s)`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to backfill");
    }
  };

  return (
    <div data-testid="advices-page" className="p-6 sm:p-8 max-w-[1400px] pb-24 sm:pb-8">
      {/* Auto-sweep green banner — shown when the UF→bank auto-sweep just
          cleared some advices on page load. Dismissible (one-time per session)
          and shows the bank name + amount swept for transparency. */}
      {QBO_ENABLED && sweepResult && sweepResult.auto_cleared > 0 && !sweepDismissed && (
        <div
          className="mb-6 border border-[#10B981]/40 bg-[#10B981]/10 text-[#065F46] px-4 py-3 flex items-start gap-3"
          data-testid="auto-sweep-banner"
        >
          <CheckCircle size={20} weight="duotone" className="text-[#10B981] flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold">
              Auto-cleared ${sweepResult.total_amount_cleared.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} across {sweepResult.auto_cleared} {sweepResult.auto_cleared === 1 ? "deposit" : "deposits"}
            </div>
            <div className="text-xs mt-0.5 text-[#065F46]/80">
              Swept from Undeposited Funds into <span className="font-semibold">{sweepResult.bank_account_name}</span>{" "}
              · {sweepResult.auto_deposits_created} QuickBooks deposit transaction{sweepResult.auto_deposits_created === 1 ? "" : "s"} created · No action needed
              {sweepResult.ambiguous?.length > 0 && (
                <span> · {sweepResult.ambiguous.length} match{sweepResult.ambiguous.length === 1 ? "" : "es"} need a human look — use &quot;Upload bank statement&quot; for those</span>
              )}
            </div>
          </div>
          <button
            onClick={() => setSweepDismissed(true)}
            className="p-1 hover:bg-[#10B981]/20 flex-shrink-0"
            title="Dismiss"
            data-testid="auto-sweep-banner-dismiss"
          >
            <X size={14} weight="bold" />
          </button>
        </div>
      )}
      <div className="mb-8 flex flex-col gap-4">
        <div>
          <div className="label-tag mb-2 flex items-center gap-2">
            <Sparkle size={12} weight="duotone" /> Auto-filled for you
            <PageHelpButton
              testId="remittances-help"
              title="How Remittances work"
              bullets={[
                <><b>Drop a customer&apos;s payment advice</b> — phone photo, PDF, or paste a screenshot. The AI fills in payer, amount, date, and invoice automatically.</>,
                <><b>Edit any row</b> if the AI misread something — click the payer or use the Edit button on the expanded row.</>,
                <><b>Bookkeeper grabs them from here</b> to work them into QuickBooks on her end. The Files page also has a content-searchable copy of every advice.</>,
                <><b>Use &quot;Ask&quot; on any row</b> if you have a question for the bookkeeper about that specific remittance — it opens an inquiry tied to this row.</>,
              ]}
              footer={<>You&apos;re uploading documents for the bookkeeper — no matching, no posting, no syncing happens here.</>}
            />
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight leading-none">
            Remittance records.
          </h1>
          <p className="text-[#525252] mt-3 max-w-xl">
            Take a photo or upload a PDF remittance advice. I&apos;ll fill in the payer, amount, date, and invoice number — the bookkeeper grabs it from here.
          </p>

          {/* Mobile-only sticky-bottom dock now lives at the bottom of the
              page — see <MobileUploadDock /> below. Always within thumb reach,
              regardless of scroll position. Helps anyone on a phone (owner,
              bookkeeper, staff) — not just the dyslexic-friendly admin. */}

          {QBO_ENABLED && qboConnected && qboCompanyName && (
            <div
              className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 bg-[#2CA01C]/[0.08] border border-[#2CA01C]/30 text-[12px]"
              data-testid="qbo-active-banner"
              title="Every QBO action on this page posts to this QuickBooks company"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#2CA01C]" />
              <span className="text-[#525252]">Posting to QuickBooks:</span>
              <span className="font-heading font-medium text-[#2CA01C]">{qboCompanyName}</span>
              <span className="text-[#a3a3a3]">·</span>
              <span className="text-[#525252]">Portal entity:</span>
              <span className="font-medium text-[#333]">{activeCompany?.name}</span>
            </div>
          )}

          {QBO_ENABLED && !qboConnected && (
            <div
              className="mt-4 flex items-start gap-2 p-3 bg-[#FEF3C7] border-l-2 border-[#F59E0B] max-w-2xl"
              data-testid="qbo-not-connected-banner"
            >
              <span className="text-[#F59E0B] font-bold leading-none mt-0.5">!</span>
              <div className="text-[12px] leading-relaxed text-[#78350F]">
                <b>QuickBooks not connected for {activeCompany?.name}.</b> You can still upload, edit, and review remittances and bank statements — but matching to QBO invoices, posting payments, and posting deposits to QuickBooks are disabled until you connect.
                {" "}<Link to="/settings" className="underline font-semibold">Connect QuickBooks in Settings →</Link>
              </div>
            </div>
          )}

          <div className="mt-4">
            <FlowStrip
              storageKey="flow-remittances-v2"
              title="What happens when you upload"
              testId="advices-flow"
              steps={REMITTANCE_FLOW_STEPS}
              footer={<>Bookkeeper sees every upload in this list and on the <Link to="/files" className="underline">Files page</Link>.</>}
            />
          </div>
          {/* Help moved into PageHelpButton next to the page title above */}
        </div>
        <div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => { handleUpload(e.target.files); e.target.value = ""; }}
            data-testid="advice-file-input"
          />
          <input
            ref={libraryRef}
            type="file"
            accept="image/*,application/pdf"
            multiple
            className="hidden"
            onChange={(e) => { handleUpload(e.target.files); e.target.value = ""; }}
            data-testid="advice-library-input"
          />
          {/* Desktop-only toolbar — two CLEARLY BORDERED CARDS side-by-side so
              the two workflows feel categorically different. Each card is its
              own contained "area" with a distinct color, heading, subtitle,
              and the buttons that belong to it. Stacks vertically on smaller
              desktops; mobile uses the sticky-bottom dock instead. */}
          <div className="hidden sm:grid sm:grid-cols-1 gap-4 max-w-2xl">
            {/* CARD 1 — Add a remittance */}
            <div
              className="border-2 border-[#171717] bg-white p-4"
              data-testid="remittance-upload-card"
            >
              <div className="flex items-center gap-2 mb-1">
                <span className="inline-flex items-center justify-center w-7 h-7 bg-[#171717] text-white text-xs font-bold">1</span>
                <h2 className="font-heading font-bold text-base text-[#171717]">Add a remittance</h2>
              </div>
              <div className="text-[12px] text-[#525252] mb-3 leading-relaxed">
                One customer&apos;s payment advice — one row per check or ACH.
              </div>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => fileRef.current?.click()}
                  className="btn-ikb px-4 py-3 flex items-center justify-center gap-2 relative"
                  data-testid="upload-advice-button"
                >
                  <Camera size={18} weight="bold" />
                  <span className="whitespace-nowrap">
                    {queue.some((q) => q.status === "uploading") ? "Take another" : "Take photo"}
                  </span>
                  {queue.filter((q) => q.status === "uploading").length > 0 && (
                    <span
                      className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 bg-[#10B981] text-white text-[10px] font-bold rounded-full inline-flex items-center justify-center"
                      data-testid="upload-advice-badge"
                    >
                      {queue.filter((q) => q.status === "uploading").length}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => libraryRef.current?.click()}
                  disabled={queue.some((q) => q.status === "uploading")}
                  className="px-4 py-3 border border-[#E5E5E5] hover:bg-[#FAFAFA] transition-all flex items-center gap-2"
                  data-testid="upload-library-button"
                >
                  <FilePdf size={18} weight="duotone" />
                  <span className="text-sm">Upload PDFs / images</span>
                </button>
              </div>
            </div>

            {/* CARD 2 — Upload bank statement (QBO-only feature) */}
            {QBO_ENABLED && (
            <div
              className={`border-2 p-4 ${qboConnected ? "border-[#002FA7] bg-[#002FA7]/[0.04]" : "border-[#E5E5E5] bg-[#FAFAFA]"}`}
              data-testid="bank-statement-upload-card"
            >
              <div className="flex items-center gap-2 mb-1">
                <span className={`inline-flex items-center justify-center w-7 h-7 text-white text-xs font-bold ${qboConnected ? "bg-[#002FA7]" : "bg-[#A3A3A3]"}`}>2</span>
                <h2 className={`font-heading font-bold text-base ${qboConnected ? "text-[#002FA7]" : "text-[#525252]"}`}>Upload bank statement</h2>
              </div>
              <div className="text-[12px] text-[#525252] mb-3 leading-relaxed">
                Your monthly statement — clears <b>many</b> applied remittances at once when deposits match.
              </div>
              <button
                onClick={() => qboConnected && setBankStmtOpen(true)}
                disabled={!qboConnected}
                className={`px-4 py-3 border flex items-center gap-2 transition-all ${
                  qboConnected
                    ? "border-[#002FA7] bg-white text-[#002FA7] hover:bg-[#002FA7]/10"
                    : "border-[#E5E5E5] bg-white text-[#A3A3A3] cursor-not-allowed"
                }`}
                data-testid="advice-upload-statement"
                title={qboConnected
                  ? "Upload your monthly bank statement (CSV/Excel/PDF) to clear applied remittances in bulk"
                  : "Connect QuickBooks Online for this entity (Settings) to enable bank-statement clearing"}
              >
                <Bank size={20} weight="duotone" />
                <span className="text-sm font-medium whitespace-nowrap">Choose CSV, Excel, or PDF</span>
              </button>
              {!qboConnected && (
                <div className="text-[11px] text-[#525252] mt-2 italic">
                  Connect QuickBooks in Settings to enable.
                </div>
              )}
            </div>
            )}
          </div>
        </div>
      </div>

      {/* Upload queue banner */}
      {queue.length > 0 && (
        <div className="mb-6 surface-card bg-white p-4" data-testid="upload-queue">
          <div className="flex items-center justify-between mb-2">
            <div className="label-tag flex items-center gap-2">
              <Sparkle size={12} weight="duotone" className="text-[#002FA7]" />
              Reading your uploads
            </div>
            <div className="text-xs text-[#525252]">
              {queue.filter(q => q.status === "done").length} / {queue.length} done
            </div>
          </div>
          <div className="space-y-1.5">
            {queue.map((q) => (
              <div key={q.id} className="flex items-center gap-2 text-sm" data-testid={`queue-${q.id}`}>
                {q.status === "uploading" && (
                  <div className="w-3 h-3 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin flex-shrink-0" />
                )}
                {q.status === "done" && <CheckCircle size={14} weight="fill" className="text-[#10B981] flex-shrink-0" />}
                {q.status === "error" && <span className="text-[#EF4444] text-xs font-bold flex-shrink-0">×</span>}
                <span className="truncate flex-1 text-[#525252]">{q.name}</span>
                {q.status === "done" && q.extracted && (
                  <span className="text-xs text-[#0A0A0A] font-medium truncate">{q.extracted}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* "Deposits go to" badge — shows the active company's chosen direct-to-bank
          account so Tessina can confirm at a glance where Payments will land. */}
      {QBO_ENABLED && qboConnected && defaultDepositName && (
        <div className="mb-3 flex items-center gap-2 text-xs" data-testid="default-deposit-badge">
          <span className="px-2 py-1 bg-[#10B981]/10 border border-[#10B981]/30 text-[#047857] font-medium">
            Deposits go to: {defaultDepositName}
          </span>
          <span className="text-[#737373]">— set in Settings → How payments land in QuickBooks</span>
        </div>
      )}

      {/* Ownership filter (page-local; defaults to All every visit) */}
      {items.length > 0 && (
        <div className="mb-3" data-testid="advices-owner-filter-wrap">
          <OwnerFilter
            value={ownerFilter}
            onChange={setOwnerFilter}
            counts={ownCounts}
            viewerRole={user?.role}
            showDone
            hideOwner
            testid="advices-owner-filter"
          />
        </div>
      )}

      {/* Filter chips + search + sync */}
      {items.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4" data-testid="advice-toolbar">
          <div className="flex flex-wrap items-center gap-1.5">
            {QBO_ENABLED && filterChips.map((c) => (
              <button
                key={c.v}
                onClick={() => setFilter(c.v)}
                data-testid={`advice-filter-${c.v}`}
                className={`px-3 py-1.5 text-xs font-medium border transition-all ${
                  filter === c.v
                    ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                    : "bg-white text-[#525252] border-[#E5E5E5] hover:border-[#0A0A0A]"
                }`}
              >
                {c.label} <span className={`ml-1 ${filter === c.v ? "text-white/70" : "text-[#A3A3A3]"}`}>{c.count}</span>
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {QBO_ENABLED && staleFlagCount > 0 && (user?.role === "owner" || user?.role === "bookkeeper") && (
              <button
                onClick={handleBackfillFlags}
                className="px-3 py-1.5 text-xs font-medium border border-[#92400E]/40 bg-[#FEF3C7] text-[#92400E] hover:bg-[#92400E] hover:text-white flex items-center gap-1.5 transition-all"
                title={`${staleFlagCount} remittance(s) are still flagged for review even though they've already been applied/cleared/deposited. Click to auto-clear those stale flags.`}
                data-testid="advice-backfill-flags"
              >
                <WarningCircle size={12} weight="duotone" />
                Fix {staleFlagCount} stale flag{staleFlagCount === 1 ? "" : "s"}
              </button>
            )}
            {QBO_ENABLED && stuckOverdueCount >= 5 && (user?.role === "owner" || user?.role === "bookkeeper") && (
              <button
                onClick={handleBulkMarkCleared}
                className="px-3 py-1.5 text-xs font-medium border border-[#F59E0B]/40 bg-[#FEF3C7] text-[#92400E] hover:bg-[#F59E0B] hover:text-white flex items-center gap-1.5 transition-all"
                title={`${stuckOverdueCount} applied-but-uncleared remittance(s) are showing Overdue. If they were reconciled in QuickBooks outside this portal, mark them all as historically cleared.`}
                data-testid="advice-bulk-mark-cleared"
              >
                <Clock size={12} weight="duotone" />
                Clean up {stuckOverdueCount} overdue
              </button>
            )}
            {(user?.role === "owner" || user?.role === "bookkeeper") && (
              <button
                onClick={handleRecoverOrphans}
                className="px-3 py-1.5 text-xs font-medium border border-[#E5E5E5] text-[#525252] hover:border-[#002FA7] hover:text-[#002FA7] flex items-center gap-1.5 transition-all"
                title="Did you upload a photo that ended up on Files but not here? Click to find any orphaned payment-advice documents and create the missing remittance rows."
                data-testid="advice-recover-orphans"
              >
                <ArrowCounterClockwise size={12} weight="duotone" />
                Recover missing
              </button>
            )}
            <div className="relative flex-1 sm:flex-initial sm:w-72 max-w-full">
              <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search payer, amount, invoice..."
                className="w-full pl-9 pr-8 py-1.5 text-sm border border-[#E5E5E5] focus:border-[#0A0A0A] focus:outline-none"
                data-testid="advice-search"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 hover:bg-[#F5F5F5]"
                  data-testid="advice-search-clear"
                >
                  <X size={12} weight="bold" className="text-[#A3A3A3]" />
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Plain-English legend for the lifecycle states (collapsed by default) */}
      {QBO_ENABLED && items.length > 0 && (
        <details className="mb-4 group" data-testid="advice-state-legend">
          <summary className="cursor-pointer text-xs text-[#525252] hover:text-[#0A0A0A] inline-flex items-center gap-1.5 select-none list-none">
            <CaretRight size={11} weight="bold" className="transition-transform group-open:rotate-90" />
            What do these states mean?
          </summary>
          <div className="mt-2 p-4 bg-[#FAFAFA] border border-[#E5E5E5] text-sm text-[#0A0A0A] space-y-2.5">
            <div className="flex gap-3">
              <span className="status-dot bg-[#F59E0B] mt-1.5 flex-shrink-0" />
              <div><b>Pending</b> — Advice received, no action yet.</div>
            </div>
            <div className="flex gap-3">
              <span className="status-dot bg-[#2CA01C] mt-1.5 flex-shrink-0" />
              <div><b>Applied</b> — Payment posted to QuickBooks, sitting in Undeposited Funds.</div>
            </div>
            <div className="flex gap-3">
              <span className="status-dot bg-[#10B981] mt-1.5 flex-shrink-0" />
              <div><b>Cleared</b> — The bank confirmed the deposit.</div>
            </div>
            <div className="flex gap-3">
              <span className="status-dot bg-[#002FA7] mt-1.5 flex-shrink-0" />
              <div>
                <b>Deposited</b> — QuickBooks Deposit transaction recorded. Money moves from <b>Undeposited Funds</b> → your bank/checking register. <span className="text-[#525252]">(Also: any advice you manually marked Deposited if it never went through QBO.)</span>
              </div>
            </div>
            <div className="flex gap-3 pt-1 border-t border-[#E5E5E5]">
              <Sparkle size={12} weight="duotone" className="text-[#002FA7] mt-1 flex-shrink-0" />
              <div><b>Sparkle on a stage</b> — That stage was done automatically by the AI (no human clicked).</div>
            </div>
            <div className="flex gap-3 pt-1 border-t border-[#E5E5E5]">
              <span className="status-dot bg-[#DC2626] mt-1.5 flex-shrink-0" />
              <div><b>Needs review</b> — Flagged for the bookkeeper. Something didn&apos;t add up (amount, customer, duplicate, etc.). Sits separately from the lifecycle.</div>
            </div>
          </div>
        </details>
      )}

      {/* Stuck-payment alert banner (only when there's something to act on) */}
      {QBO_ENABLED && counts.stuck > 0 && (
        <div className="mb-4 bg-[#FEF3C7] border-l-2 border-[#F59E0B]" data-testid="advice-stuck-banner">
          <div className="p-3 flex items-start gap-3">
            <Clock size={18} weight="fill" className="text-[#F59E0B] mt-0.5 flex-shrink-0" />
            <div className="flex-1 text-sm">
              <span className="font-semibold text-[#78350F]">{counts.stuck} payment{counts.stuck > 1 ? "s" : ""} posted to QuickBooks 7+ days ago haven&apos;t cleared the bank yet.</span>
              <span className="text-[#92400E]"> This usually means the deposit hasn&apos;t been recorded in QuickBooks, or the check bounced.</span>
              <button
                type="button"
                onClick={() => setShowStuckList((v) => !v)}
                className="ml-2 text-[#92400E] underline decoration-dotted hover:no-underline font-medium"
                data-testid="advice-stuck-toggle"
              >
                {showStuckList ? "Hide list" : `Show which ${counts.stuck > 1 ? "ones" : "one"}`}
              </button>
            </div>
            <button
              onClick={() => setFilter("applied")}
              className="text-xs px-2 py-1 bg-white border border-[#F59E0B] text-[#92400E] hover:bg-[#FEF3C7] flex-shrink-0"
              data-testid="advice-stuck-view"
            >
              Review
            </button>
          </div>
          {showStuckList && (
            <ul className="border-t border-[#F59E0B]/40 bg-white/40 divide-y divide-[#F59E0B]/30" data-testid="advice-stuck-list">
              {stuckItems.map((a) => {
                const ageDays = a.applied_at
                  ? Math.floor((Date.now() - new Date(a.applied_at).getTime()) / (1000 * 60 * 60 * 24))
                  : null;
                return (
                  <li key={a.id} className="px-3 py-2 flex items-center gap-3 text-sm" data-testid={`advice-stuck-item-${a.id}`}>
                    <button
                      type="button"
                      onClick={() => {
                        setExpandedIds((m) => ({ ...m, [a.id]: true }));
                        setSearch(a.payer_name || a.invoice_number || "");
                        setTimeout(() => {
                          const el = document.querySelector(`[data-testid="advice-row-${a.id}"]`);
                          if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
                        }, 200);
                      }}
                      className="font-medium text-[#0A0A0A] hover:text-[#002FA7] underline decoration-dotted hover:no-underline truncate flex-1 text-left"
                      title="Jump to this row"
                    >
                      {a.payer_name || <span className="italic text-[#A3A3A3]">No payer</span>}
                    </button>
                    {a.invoice_number && (
                      <span className="font-mono text-xs text-[#525252] truncate hidden sm:inline">#{a.invoice_number}</span>
                    )}
                    <span className="text-xs text-[#525252] whitespace-nowrap">
                      ${Number(a.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                    <span className="text-xs text-[#92400E] whitespace-nowrap">
                      applied {ageDays != null ? `${ageDays}d ago` : "—"}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {/* Stuck-in-Undeposited-Funds banner (Payment posted, no QBO Deposit recorded after 3+ days) */}
      {QBO_ENABLED && counts.in_undeposited > 0 && (
        <div className="mb-4 bg-[#FEF3C7] border-l-2 border-[#F59E0B]" data-testid="advice-in-undeposited-banner">
          <div className="p-3 flex items-start gap-3">
            <Bank size={18} weight="fill" className="text-[#F59E0B] mt-0.5 flex-shrink-0" />
            <div className="flex-1 text-sm">
              <span className="font-semibold text-[#78350F]">{counts.in_undeposited} payment{counts.in_undeposited > 1 ? "s are" : " is"} parked in QuickBooks Undeposited Funds.</span>
              <span className="text-[#92400E]"> Posted 3+ days ago but no Deposit transaction was recorded — the funds aren&apos;t in your bank register yet. Upload a bank statement (with <b>&quot;Create QBO Deposit&quot;</b> ticked) to move them.</span>
              <button
                type="button"
                onClick={() => setShowInUndepositedList((v) => !v)}
                className="ml-2 text-[#92400E] underline decoration-dotted hover:no-underline font-medium"
                data-testid="advice-in-undeposited-toggle"
              >
                {showInUndepositedList ? "Hide list" : `Show which ${counts.in_undeposited > 1 ? "ones" : "one"}`}
              </button>
            </div>
            {qboConnected && (
              <button
                onClick={() => setBankStmtOpen(true)}
                className="text-xs px-2 py-1 bg-white border border-[#F59E0B] text-[#92400E] hover:bg-[#FEF3C7] flex-shrink-0 whitespace-nowrap"
                data-testid="advice-in-undeposited-fix"
              >
                Upload statement
              </button>
            )}
          </div>
          {showInUndepositedList && (
            <ul className="border-t border-[#F59E0B]/40 bg-white/40 divide-y divide-[#F59E0B]/30" data-testid="advice-in-undeposited-list">
              {inUndepositedItems.map((a) => {
                const ageDays = a.applied_at
                  ? Math.floor((Date.now() - new Date(a.applied_at).getTime()) / (1000 * 60 * 60 * 24))
                  : null;
                return (
                  <li key={a.id} className="px-3 py-2 flex items-center gap-3 text-sm" data-testid={`advice-in-undeposited-item-${a.id}`}>
                    <button
                      type="button"
                      onClick={() => {
                        setExpandedIds((m) => ({ ...m, [a.id]: true }));
                        setSearch(a.payer_name || a.invoice_number || "");
                        // Smooth-scroll to the row after the filter rerenders
                        setTimeout(() => {
                          const el = document.querySelector(`[data-testid="advice-row-${a.id}"]`);
                          if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
                        }, 200);
                      }}
                      className="font-medium text-[#0A0A0A] hover:text-[#002FA7] underline decoration-dotted hover:no-underline truncate flex-1 text-left"
                      title="Jump to this row"
                    >
                      {a.payer_name || <span className="italic text-[#A3A3A3]">No payer</span>}
                    </button>
                    {a.invoice_number && (
                      <span className="font-mono text-xs text-[#525252] truncate hidden sm:inline">#{a.invoice_number}</span>
                    )}
                    <span className="text-xs text-[#525252] whitespace-nowrap">
                      ${Number(a.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                    <span className="text-xs text-[#92400E] whitespace-nowrap">
                      applied {ageDays != null ? `${ageDays}d ago` : "—"}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {/* Compact list view */}
      <div className="surface-card bg-white" data-testid="advice-list">
        {items.length === 0 ? (
          <div className="p-10 text-center">
            <Receipt size={36} weight="duotone" className="mx-auto text-[#525252] mb-3" />
            <div className="font-heading text-lg">No remittance records yet.</div>
            <div className="text-sm text-[#525252] mt-1">Take a photo or upload a PDF remittance advice to begin.</div>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="p-10 text-center text-sm text-[#525252]">
            No remittances match these filters.
            <button
              onClick={() => { setFilter("all"); setSearch(""); setOwnerFilter("all"); }}
              className="ml-2 underline text-[#002FA7]"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <div className="divide-y divide-[#E5E5E5]">
            {filteredItems.map((a) => {
              const isEditing = !!editing[a.id];
              const e = editing[a.id] || {};
              const tone = statusTone[a.status] || statusTone.pending;
              const isExpanded = !!expandedIds[a.id] || isEditing;
              const isCleared = !!a.qbo_cleared_at;
              const isApplied = a.status === "applied" || a.status === "matched";
              // Period that this advice belongs to ("YYYY-MM"). Prefer the
              // advice's own `date` (when the customer paid), fall back to
              // applied_at (when we recorded the payment) so even un-dated
              // remittances respect close-period suppression.
              const adviceMonth = (a.date || a.applied_at || "").slice(0, 7);
              const inClosedPeriod = adviceMonth && closedMonths.has(adviceMonth);
              const isStuck =
                isApplied && !isCleared && a.applied_at && !inClosedPeriod &&
                (Date.now() - new Date(a.applied_at).getTime()) / (1000 * 60 * 60 * 24) >= 7;
              const actor = nextActorForRemittance(a);
              const viewerOwns = actor !== null && actor === ownBucketFor(user?.role);
              return (
                <div key={a.id} data-testid={`advice-${a.id}`} style={ownerLeftStripe(actor)}>
                  {/* LIFECYCLE STEPPER (desktop only — too cramped on mobile) */}
                  {QBO_ENABLED && (
                    <div
                      onClick={() => !isEditing && toggleExpand(a.id)}
                      className="px-4 pt-2.5 pb-1 hidden md:flex items-center justify-end hover:bg-[#FAFAF7] cursor-pointer"
                      title="Click row to expand"
                    >
                      <LifecycleStepper advice={a} />
                    </div>
                  )}
                  {/* COMPACT ROW — no expand/collapse. Every common action is
                      a visible button on the right of the row. Edit pencil
                      opens an inline form below; no caret to click. */}
                  <div
                    className="px-4 pb-3 pt-3 flex items-center gap-3 flex-wrap"
                    data-testid={`advice-row-${a.id}`}
                  >
                    {QBO_ENABLED && (() => {
                      // Status pill: hidden on desktop (the lifecycle stepper above
                      // is the source of truth). Kept on mobile because the stepper
                      // is hidden on small screens to keep the row dense.
                      // Pill priority must mirror the lifecycle stepper:
                      // Deposited > Cleared > Applied/matched > pending status.
                      const isDeposited = !!a.qbo_deposit_id || a.status === "deposited";
                      const showAutoSparkle = a.qbo_auto_applied && (isApplied || isCleared || isDeposited);
                      if (isDeposited) {
                        return (
                          <span className="md:hidden inline-flex items-center gap-1.5 px-2 py-0.5 bg-[#002FA7]/10 text-[#002FA7] text-[10px] font-semibold tracking-wider uppercase flex-shrink-0" data-testid={`advice-deposited-badge-${a.id}`}>
                            <CheckCircle size={11} weight="fill" />
                            Deposited{showAutoSparkle ? <Sparkle size={10} weight="duotone" className="ml-0.5" /> : null}
                          </span>
                        );
                      }
                      if (isCleared) {
                        return (
                          <span className="md:hidden inline-flex items-center gap-1.5 px-2 py-0.5 bg-[#10B981]/10 text-[#10B981] text-[10px] font-semibold tracking-wider uppercase flex-shrink-0" data-testid={`advice-cleared-badge-${a.id}`}>
                            <CheckCircle size={11} weight="fill" />
                            Cleared{showAutoSparkle ? <Sparkle size={10} weight="duotone" className="ml-0.5" /> : null}
                          </span>
                        );
                      }
                      return (
                        <span
                          className={`md:hidden inline-flex items-center gap-1.5 px-2 py-0.5 ${tone.bg} ${tone.text} text-[10px] font-semibold tracking-wider uppercase flex-shrink-0`}
                          data-testid={isApplied ? `advice-applied-badge-${a.id}` : undefined}
                        >
                          <span className={`status-dot ${tone.dot}`} />
                          {a.status === "matched" ? "applied" : a.status}{showAutoSparkle ? <Sparkle size={10} weight="duotone" className="ml-0.5" /> : null}
                        </span>
                      );
                    })()}
                    {/* OwnerBadge: only show when the row is still pending or
                        waiting on a human. Once Applied/Cleared/Deposited, the
                        lifecycle stepper makes the next actor implicit and the
                        badge becomes noise. */}
                    {QBO_ENABLED && actor && !isApplied && !isCleared && !(a.qbo_deposit_id || a.status === "deposited") && (
                      <OwnerBadge actor={actor} viewerIsOwn={viewerOwns} compact />
                    )}
                    {QBO_ENABLED && isStuck && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-[#FEF3C7] text-[#92400E] text-[10px] font-semibold flex-shrink-0" title="Posted 7+ days ago — bank deposit not yet recorded in QBO">
                        <Clock size={10} weight="fill" /> <span className="hidden sm:inline">Overdue</span>
                      </span>
                    )}
                    {QBO_ENABLED && a.qbo_deposit_create_failed && (
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-[#FEF3C7] text-[#92400E] text-[10px] font-semibold flex-shrink-0"
                        title="This remittance is cleared in the portal, but the QBO Deposit transaction failed to create. The Payment still sits in QuickBooks Undeposited Funds — bookkeeper needs to create the deposit manually in QBO."
                        data-testid={`advice-deposit-failed-${a.id}`}
                      >
                        <WarningCircle size={10} weight="fill" /> <span className="hidden sm:inline">QBO deposit needed</span>
                      </span>
                    )}
                    {QBO_ENABLED && a.review_status === "needs_review" && (
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-[#FEF3C7] text-[#92400E] text-[10px] font-semibold flex-shrink-0"
                        title={a.review_reason || "Bookkeeper review required before applying"}
                        data-testid={`advice-review-badge-${a.id}`}
                      >
                        <WarningCircle size={10} weight="fill" /> <span className="hidden sm:inline">Bookkeeper review</span>
                        {(user?.role === "owner" || user?.role === "bookkeeper") && (
                          <button
                            onClick={(ev) => { ev.stopPropagation(); handleClearReview(a); }}
                            className="ml-0.5 -mr-0.5 hover:bg-[#92400E] hover:text-white p-0.5 transition-colors leading-none"
                            title="Clear bookkeeper-review flag — this remittance does not need review"
                            data-testid={`advice-review-clear-${a.id}`}
                          >
                            <X size={10} weight="bold" />
                          </button>
                        )}
                      </span>
                    )}
                    {/* AI Auto-applied badge merged into the status pill (mobile)
                        and the lifecycle stepper (desktop) as a small sparkle
                        marker — no longer a standalone pill. */}
                    <div className="flex-1 min-w-0 flex items-baseline gap-2">
                      <div className="font-medium text-sm truncate">
                        {a.payer_name || <span className="text-[#A3A3A3] italic">No payer</span>}
                      </div>
                      <button
                        type="button"
                        onClick={(ev) => {
                          ev.stopPropagation();
                          setExpandedIds((m) => ({ ...m, [a.id]: true }));
                          beginEdit(a);
                        }}
                        title="Edit payer / amount / date / invoice"
                        aria-label="Edit"
                        className="flex-shrink-0 inline-flex items-center justify-center gap-1 min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 px-3 py-2 sm:px-1.5 sm:py-0.5 text-[10px] font-semibold tracking-wide uppercase text-[#525252] hover:text-white hover:bg-[#002FA7] border border-[#E5E5E5] hover:border-[#002FA7] transition-colors"
                        data-testid={`advice-edit-payer-${a.id}`}
                      >
                        <PencilSimple size={18} weight="bold" className="sm:!w-[10px] sm:!h-[10px]" />
                        <span className="hidden sm:inline">Edit</span>
                      </button>
                      <div className="hidden sm:block text-xs text-[#525252] truncate">
                        {a.invoice_number || ""}
                      </div>
                    </div>
                    <div className="font-mono text-sm text-right flex-shrink-0 hidden sm:block">
                      {a.date || "—"}
                    </div>
                    <div className="font-mono text-sm font-bold text-right flex-shrink-0 w-24">
                      {a.amount != null ? `$${Number(a.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : "—"}
                    </div>
                    <div className="flex flex-col gap-1 flex-shrink-0 items-stretch w-[88px]">
                      {a.document_id && (
                        <button
                          onClick={(ev) => { ev.stopPropagation(); setViewerStartId(a.document_id); }}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#002FA7]/10 text-[#002FA7] hover:bg-[#002FA7] hover:text-white text-[11px] font-semibold tracking-wider uppercase border border-[#002FA7]/30 transition-colors justify-center"
                          title="View remittance"
                          data-testid={`advice-view-${a.id}`}
                        >
                          <span className="status-dot bg-current" /> View
                        </button>
                      )}
                      <button
                        onClick={(ev) => { ev.stopPropagation(); openAskDialog(a); }}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#002FA7]/10 text-[#002FA7] hover:bg-[#002FA7] hover:text-white text-[11px] font-semibold tracking-wider uppercase border border-[#002FA7]/30 transition-colors justify-center"
                        title="Ask a question about this remittance"
                        data-testid={`advice-ask-${a.id}`}
                      >
                        <span className="status-dot bg-current" /> Ask
                      </button>
                      <button
                        onClick={(ev) => { ev.stopPropagation(); handleMoveToFiles(a); }}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#92400E]/10 text-[#92400E] hover:bg-[#92400E] hover:text-white text-[11px] font-semibold tracking-wider uppercase border border-[#92400E]/30 transition-colors justify-center"
                        title="Move to Files — keeps the file, drops the row"
                        data-testid={`advice-move-to-files-${a.id}`}
                      >
                        <span className="status-dot bg-current" /> To files
                      </button>
                      <button
                        onClick={(ev) => { ev.stopPropagation(); handleDelete(a.id); }}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#DC2626]/10 text-[#DC2626] hover:bg-[#DC2626] hover:text-white text-[11px] font-semibold tracking-wider uppercase border border-[#DC2626]/30 transition-colors justify-center"
                        title="Delete — removes the row AND the file"
                        data-testid={`advice-delete-${a.id}`}
                      >
                        <span className="status-dot bg-current" /> Delete
                      </button>
                    </div>
                  </div>

                  {/* Paid-in-QBO hint — surfaced when the user's invoice number
                      matches a CLOSED/paid invoice in QBO. Tells them the row
                      will never clear because QBO already considers it paid. */}
                  {QBO_ENABLED && a.qbo_paid_invoice_hint && !a.qbo_payment_id && (
                    <div
                      className="mx-4 mb-2 px-3 py-2 bg-[#FEF3C7] border-l-4 border-[#92400E] text-[12px] text-[#92400E] flex items-start gap-2"
                      data-testid={`advice-paid-hint-${a.id}`}
                    >
                      <WarningCircle size={16} weight="fill" className="flex-shrink-0 mt-[1px]" />
                      <div className="flex-1 leading-snug">
                        <span className="font-semibold">
                          Invoice {a.qbo_paid_invoice_hint.doc_number} is already marked paid in QuickBooks
                        </span>
                        {a.qbo_paid_invoice_hint.customer_name && (
                          <> for <span className="font-medium">{a.qbo_paid_invoice_hint.customer_name}</span></>
                        )}
                        {a.qbo_paid_invoice_hint.txn_date && (
                          <> (invoice dated {a.qbo_paid_invoice_hint.txn_date})</>
                        )}.
                        <span className="block mt-0.5 text-[#525252] font-normal">
                          That's why the row can't clear here. Either un-apply the payment in QBO if this is a mistake, or click <span className="font-semibold">To files</span> to keep the advice as a file without leaving a stuck row.
                        </span>
                      </div>
                    </div>
                  )}

                  {/* EXPANDED DETAIL */}
                  {isExpanded && (
                    <div className="px-4 pb-4 pt-1 bg-[#FAFAF7] border-t border-[#F5F5F5]" data-testid={`advice-detail-${a.id}`}>
                      <div className="flex items-center gap-2 mb-3 flex-wrap">
                        {a.payer_name && (
                          <span className="label-tag text-[10px] flex items-center gap-1">
                            <Sparkle size={10} weight="duotone" /> Auto-filled
                          </span>
                        )}
                        {a.uploaded_by_name && (
                          <span className="label-tag text-[10px] text-[#525252]">
                            by {a.uploaded_by_name}
                          </span>
                        )}
                        {a.document_id && (
                          <button
                            type="button"
                            onClick={(ev) => { ev.stopPropagation(); handleReExtract(a.id); }}
                            disabled={!!reExtracting[a.id]}
                            className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 border border-[#002FA7]/30 text-[#002FA7] hover:bg-[#002FA7] hover:text-white text-[10px] font-semibold tracking-wider uppercase transition-colors disabled:opacity-50"
                            title="Re-run AI on the original file (uses the latest, smarter prompt)"
                            data-testid={`advice-re-extract-${a.id}`}
                          >
                            <ArrowsClockwise size={10} weight="bold" className={reExtracting[a.id] ? "animate-spin" : ""} />
                            {reExtracting[a.id] ? "Re-running…" : "Re-run AI"}
                          </button>
                        )}
                      </div>
                      {isEditing ? (
                        <div className="space-y-3 max-w-xl">
                          <div>
                            <div className="label-tag mb-1">Payer</div>
                            <input
                              value={e.payer_name}
                              onChange={(ev) => setEditing((s) => ({ ...s, [a.id]: { ...e, payer_name: ev.target.value } }))}
                              className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#002FA7] focus:outline-none text-sm"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <div className="label-tag mb-1">Amount</div>
                              <input
                                type="number"
                                step="0.01"
                                value={e.amount}
                                onChange={(ev) => setEditing((s) => ({ ...s, [a.id]: { ...e, amount: ev.target.value } }))}
                                className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#002FA7] focus:outline-none text-sm"
                              />
                            </div>
                            <div>
                              <div className="label-tag mb-1">Date</div>
                              <input
                                type="date"
                                value={e.date || ""}
                                onChange={(ev) => setEditing((s) => ({ ...s, [a.id]: { ...e, date: ev.target.value } }))}
                                className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#002FA7] focus:outline-none text-sm"
                              />
                            </div>
                          </div>
                          <div>
                            <div className="label-tag mb-1">Invoice #</div>
                            <input
                              value={e.invoice_number}
                              onChange={(ev) => setEditing((s) => ({ ...s, [a.id]: { ...e, invoice_number: ev.target.value } }))}
                              className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#002FA7] focus:outline-none text-sm"
                            />
                          </div>
                          <div className="flex gap-2 pt-2">
                            <button
                              onClick={() => handleSave(a.id)}
                              className="btn-ikb px-3 py-1.5 text-xs"
                              data-testid={`advice-save-${a.id}`}
                            >Save</button>
                            <button
                              onClick={() => setEditing((s) => ({ ...s, [a.id]: undefined }))}
                              className="px-3 py-1.5 text-xs border border-[#E5E5E5] hover:bg-white"
                            >Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center gap-2 flex-wrap">
                            <button
                              onClick={() => beginEdit(a)}
                              className="px-3 py-1.5 text-xs border border-[#E5E5E5] hover:bg-white bg-white"
                              data-testid={`advice-edit-${a.id}`}
                            >Edit</button>
                          </div>
                          {/* Prominent "Invoice #" line on expanded view — empty state nudges user to fix it */}
                          <div className="mt-3 flex items-center gap-2 text-sm" data-testid={`advice-inv-line-${a.id}`}>
                            <span className="label-tag text-[10px]">Invoice #</span>
                            {a.invoice_number ? (
                              <span className="font-mono font-semibold text-[#0A0A0A]">{a.invoice_number}</span>
                            ) : (
                              <>
                                <span className="italic text-[#F59E0B]">not detected</span>
                                <button
                                  onClick={() => beginEdit(a)}
                                  className="text-xs text-[#002FA7] underline decoration-dotted hover:no-underline"
                                  data-testid={`advice-add-invoice-${a.id}`}
                                  title="Add the invoice number from the remittance so QBO can pin it at 100%."
                                >add it for an exact match</button>
                              </>
                            )}
                          </div>
                          {QBO_ENABLED && qboConnected && (
                            <QboMatchPanel
                              advice={a}
                              isOwner={user?.role === "owner"}
                              isStaff={user?.role === "staff"}
                              onApplied={load}
                              onReviewApproved={load}
                            />
                          )}
                          {QBO_ENABLED && qboConnected && showAdvancedBank && a.qbo_payment_id && a.qbo_cleared_at && !a.qbo_deposit_id && (
                            <button
                              onClick={() => setRecreateAdvice(a)}
                              className="mt-3 w-full text-xs px-3 py-2 border border-[#F59E0B]/40 bg-[#FEF3C7] text-[#92400E] hover:bg-[#FDE68A] flex items-center justify-center gap-2"
                              data-testid={`advice-recreate-deposit-${a.id}`}
                              title="This advice was cleared via bank statement but no QBO Deposit was created. Click to post one now."
                            >
                              <UploadSimple size={12} weight="bold" />
                              Re-create QBO Deposit (none was posted)
                            </button>
                          )}
                          {/* Mark cleared — manual safety valve for owner/bookkeeper when
                              the auto-pipeline misses something. Portal-only flag (no QBO
                              mutation). Only on Applied rows that aren't already cleared. */}
                          {QBO_ENABLED && (user?.role === "owner" || user?.role === "bookkeeper") &&
                            a.qbo_payment_id && !a.qbo_cleared_at && (
                            <button
                              onClick={async () => {
                                if (!window.confirm(
                                  `Mark this ${a.payer_name || "remittance"} ($${a.amount}) as cleared?\n\nThis updates the portal only — it does not change anything in QuickBooks. Use this when you've confirmed the deposit landed but the portal missed it.`
                                )) return;
                                try {
                                  await api.post(`/qbo/mark-cleared/${a.id}`, {});
                                  toast.success("Marked cleared");
                                  load();
                                } catch (e) {
                                  toast.error(e?.response?.data?.detail || "Failed to mark cleared");
                                }
                              }}
                              className="mt-2 w-full text-xs px-3 py-2 border border-[#10B981]/40 bg-[#10B981]/10 text-[#065F46] hover:bg-[#10B981]/20 flex items-center justify-center gap-2"
                              data-testid={`advice-mark-cleared-${a.id}`}
                              title="Manually mark this as cleared (portal-only — does not touch QBO)"
                            >
                              <CheckCircle size={12} weight="bold" />
                              Mark as cleared
                            </button>
                          )}
                          {/* Unmark cleared — only shown on rows manually cleared. Auto-cleared
                              ones reflect QBO state and shouldn't be undone here. */}
                          {QBO_ENABLED && (user?.role === "owner" || user?.role === "bookkeeper") &&
                            a.qbo_cleared_at && a.qbo_clearance_via === "manual" && (
                            <button
                              onClick={async () => {
                                if (!window.confirm("Undo manual clearance? This brings the remittance back to Applied.")) return;
                                try {
                                  await api.post(`/qbo/unmark-cleared/${a.id}`);
                                  toast.success("Undid manual clearance");
                                  load();
                                } catch (e) {
                                  toast.error(e?.response?.data?.detail || "Failed to undo");
                                }
                              }}
                              className="mt-2 w-full text-xs px-3 py-2 border border-[#E5E5E5] text-[#525252] hover:bg-[#F5F5F5] flex items-center justify-center gap-2"
                              data-testid={`advice-unmark-cleared-${a.id}`}
                              title="Reverse this manual clearance"
                            >
                              Undo manual clear
                            </button>
                          )}
                          <details className="mt-4 group" data-testid={`advice-history-toggle-${a.id}`}>
                            <summary className="text-xs font-medium text-[#525252] hover:text-[#0A0A0A] cursor-pointer select-none flex items-center gap-1">
                              <CaretRight size={10} weight="bold" className="transition-transform group-open:rotate-90" />
                              History ({a.id ? "this remittance" : ""})
                            </summary>
                            <div className="mt-3 pl-1">
                              <EntityHistory companyId={activeCompanyId} resourceType="remittance" resourceId={a.id} compact />
                            </div>
                          </details>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      <DocumentViewer
        open={!!viewerStartId}
        onClose={() => setViewerStartId(null)}
        docs={viewerDocs}
        startId={viewerStartId}
      />
      {QBO_ENABLED && (
        <BankStatementUploadDialog
          open={bankStmtOpen}
          onClose={() => setBankStmtOpen(false)}
          companyId={activeCompanyId}
          onApplied={load}
        />
      )}
      {QBO_ENABLED && (
        <RecreateDepositDialog
          advice={recreateAdvice}
          companyId={activeCompanyId}
          onClose={() => setRecreateAdvice(null)}
          onDone={() => { setRecreateAdvice(null); load(); }}
        />
      )}

      <Dialog
        open={!!askAdvice}
        onOpenChange={(v) => { if (!v) { setAskAdvice(null); setAskTitle(""); setAskBody(""); } }}
      >
        <DialogContent className="rounded-none max-w-lg" data-testid="advice-ask-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <ChatCircleDots size={20} weight="duotone" className="text-[#002FA7]" />
              Ask about this remittance
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="bg-[#F5F5F5] p-3 border-l-2 border-[#002FA7]">
              <div className="label-tag text-[10px] mb-1">Linked remittance</div>
              <div className="font-heading font-medium">{askAdvice ? adviceLabel(askAdvice) : ""}</div>
              {askAdvice?.invoice_number && (
                <div className="label-tag text-[10px] text-[#525252] mt-0.5 font-mono">
                  Invoice: {askAdvice.invoice_number}
                </div>
              )}
              {askAdvice?.document_id && (
                <div className="label-tag text-[10px] text-[#525252] mt-0.5 truncate font-mono">
                  📎 Document attached
                </div>
              )}
            </div>
            <div>
              <label className="label-tag block mb-2">Subject</label>
              <input
                value={askTitle}
                onChange={(e) => setAskTitle(e.target.value)}
                className="w-full px-3 py-2.5 border border-[#E5E5E5] focus:border-[#002FA7] focus:outline-none"
                data-testid="advice-ask-title"
              />
            </div>
            <div>
              <label className="label-tag block mb-2">What do you need?</label>
              <textarea
                value={askBody}
                onChange={(e) => setAskBody(e.target.value)}
                rows={4}
                placeholder="e.g. I see a $50 short-pay — was a credit memo applied?"
                className="w-full px-3 py-2.5 border border-[#E5E5E5] focus:border-[#002FA7] focus:outline-none resize-none"
                data-testid="advice-ask-body"
              />
            </div>
            <div>
              <label className="label-tag block mb-2">Who&rsquo;s this for?</label>
              <DirectedToPicker
                value={askDirectedTo}
                onChange={setAskDirectedTo}
                testid="advice-ask-directed"
              />
              <div className="text-[11px] text-[#525252] mt-1">
                Only that person gets the email/in-app notification.
              </div>
            </div>
            <p className="text-[11px] text-[#525252]">
              The inquiry will appear on the Inquiries page with the remittance details already attached.
            </p>
          </div>
          <DialogFooter>
            <button
              onClick={() => { setAskAdvice(null); setAskTitle(""); setAskBody(""); }}
              className="px-4 py-2 border border-[#E5E5E5]"
              data-testid="advice-ask-cancel"
            >
              Cancel
            </button>
            <button
              onClick={submitAsk}
              disabled={askSubmitting || !askTitle.trim()}
              className="btn-ikb px-4 py-2 disabled:opacity-60"
              data-testid="advice-ask-submit"
            >
              {askSubmitting ? "Submitting…" : "Submit inquiry"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mobile-only sticky upload dock — always within thumb reach, regardless
          of how far the user has scrolled. Helps every role (owner, bookkeeper,
          staff) on a phone, not just the dyslexic-friendly admin. */}
      <div
        className="sm:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-[#E5E5E5] shadow-[0_-2px_8px_rgba(0,0,0,0.08)] pb-[env(safe-area-inset-bottom)]"
        data-testid="mobile-upload-dock"
      >
        <div className="flex gap-2 p-3">
          <button
            onClick={() => fileRef.current?.click()}
            className="btn-ikb flex-1 px-3 py-4 flex items-center justify-center gap-2 text-base font-semibold relative"
            data-testid="mobile-dock-photo"
            aria-label="Take photo of a remittance"
          >
            <Camera size={20} weight="bold" />
            <span>{queue.some((q) => q.status === "uploading") ? "Take another" : "Photo"}</span>
            {queue.filter((q) => q.status === "uploading").length > 0 && (
              <span
                className="absolute top-1 right-2 min-w-[20px] h-5 px-1 bg-[#10B981] text-white text-[10px] font-bold rounded-full inline-flex items-center justify-center"
                data-testid="mobile-dock-photo-badge"
              >
                {queue.filter((q) => q.status === "uploading").length}
              </span>
            )}
          </button>
          <button
            onClick={() => libraryRef.current?.click()}
            disabled={queue.some((q) => q.status === "uploading")}
            className="flex-1 px-3 py-4 border border-[#E5E5E5] bg-white hover:bg-[#FAFAFA] active:bg-[#F5F5F5] text-[#525252] flex items-center justify-center gap-1.5 disabled:opacity-50"
            data-testid="mobile-dock-files"
            aria-label="Upload remittance PDFs or images"
          >
            <FilePdf size={20} weight="duotone" />
            <span className="text-sm font-medium">Files</span>
          </button>
          {QBO_ENABLED && (
          <button
            onClick={() => qboConnected && setBankStmtOpen(true)}
            disabled={!qboConnected}
            className={`flex-1 px-3 py-4 border flex items-center justify-center gap-2 text-base font-semibold ${
              qboConnected
                ? "border-[#002FA7]/40 bg-[#002FA7]/10 text-[#002FA7] active:bg-[#002FA7]/20"
                : "border-[#E5E5E5] bg-[#FAFAFA] text-[#A3A3A3]"
            }`}
            data-testid="mobile-dock-statement"
            aria-label="Upload bank statement"
            title={qboConnected ? "Upload your monthly bank statement" : "Connect QuickBooks to enable"}
          >
            <Bank size={20} weight="duotone" />
            <span>Bank stmt</span>
          </button>
          )}
        </div>
      </div>
    </div>
  );
}

function RecreateDepositDialog({ advice, companyId, onClose, onDone }) {
  const [bankAccounts, setBankAccounts] = useState([]);
  const [bankAccountId, setBankAccountId] = useState("");
  const [depositDate, setDepositDate] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!advice || !companyId) return;
    setDepositDate(advice.qbo_cleared_at || new Date().toISOString().slice(0, 10));
    api.get(`/qbo/bank-accounts?company_id=${companyId}`)
      .then(({ data }) => {
        const accts = data.accounts || [];
        setBankAccounts(accts);
        const remembered = localStorage.getItem(`bank-acct-${companyId}`);
        const rememberedAcct = remembered ? accts.find((a) => a.id === remembered) : null;
        if (rememberedAcct) setBankAccountId(rememberedAcct.id);
        else if (accts.length > 0) setBankAccountId(accts[0].id);
      })
      .catch(() => setBankAccounts([]));
  }, [advice, companyId]);

  if (!advice) return null;

  const submit = async () => {
    // Pre-flight validation so the user gets a specific, actionable message
    // instead of the backend's generic "Field required" 422 error.
    if (!bankAccountId) {
      toast.error("Please pick a bank account before creating the deposit.");
      return;
    }
    if (!depositDate) {
      toast.error("Please pick a deposit date before creating the deposit.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/qbo/recreate-deposit/${advice.id}`, {
        bank_account_id: bankAccountId,
        deposit_date: depositDate || undefined,
      });
      try { localStorage.setItem(`bank-acct-${companyId}`, bankAccountId); } catch (err) { console.debug("localStorage write skipped:", err); }
      toast.success(`QBO Deposit ${data.qbo_deposit_id} created in ${data.bank_account_name}`);
      onDone?.();
    } catch (e) {
      // Defensive: if the global Axios interceptor didn't normalize a Pydantic 422
      // body, do it locally so the user sees WHICH field failed instead of a bare
      // "Field required". Also log full payload for diagnosis.
      console.error("recreate-deposit failed:", e?.response?.status, e?.response?.data);
      const detail = e?.response?.data?.detail;
      let msg = "Failed to create deposit";
      if (typeof detail === "string") {
        msg = detail;
      } else if (Array.isArray(detail)) {
        msg = detail
          .map((it) => {
            const m = typeof it?.msg === "string" ? it.msg : JSON.stringify(it);
            const path = Array.isArray(it?.loc)
              ? it.loc.filter((p) => p !== "body" && p !== "query" && p !== "path").join(".")
              : "";
            return path ? `${path}: ${m}` : m;
          })
          .join(" · ");
      } else if (detail && typeof detail.msg === "string") {
        msg = detail.msg;
      } else if (e?.response?.status) {
        msg = `Failed to create deposit (HTTP ${e.response.status})`;
      }
      toast.error(msg);
    }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="recreate-deposit-dialog">
      <div className="bg-white max-w-md w-full p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="font-heading text-lg font-medium">Re-create QBO Deposit</div>
        <div className="text-xs text-[#525252]">
          This advice was already cleared, but no QuickBooks Deposit was posted at the time. Pick the bank account and date below to post the deposit now. The matching Payment will be pulled out of Undeposited Funds into the bank register.
        </div>
        <div className="bg-[#FAFAFA] p-3 text-xs space-y-0.5">
          <div><b>Payer:</b> {advice.payer_name || "—"}</div>
          <div><b>Amount:</b> ${Number(advice.amount || 0).toFixed(2)}</div>
          <div><b>QBO Payment ID:</b> {advice.qbo_payment_id}</div>
        </div>
        <div>
          <div className="label-tag mb-1">Bank account</div>
          <select
            value={bankAccountId}
            onChange={(e) => setBankAccountId(e.target.value)}
            className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#002FA7] focus:outline-none text-sm bg-white"
            data-testid="recreate-deposit-account"
          >
            {bankAccounts.length === 0 && <option value="">Loading…</option>}
            {/* Explicit placeholder when state is empty — prevents the browser from
                silently displaying the first <option> as selected while leaving
                bankAccountId="" (the source of phantom "Field required" errors). */}
            {bankAccounts.length > 0 && bankAccountId === "" && (
              <option value="" disabled>— Pick a bank account —</option>
            )}
            {bankAccounts.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
        </div>
        <div>
          <div className="label-tag mb-1">Deposit date</div>
          <input
            type="date"
            value={depositDate}
            onChange={(e) => setDepositDate(e.target.value)}
            className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#002FA7] focus:outline-none text-sm"
            data-testid="recreate-deposit-date"
          />
        </div>
        <div className="flex gap-2 pt-2 justify-end">
          <button onClick={onClose} className="px-3 py-1.5 text-xs border border-[#E5E5E5] hover:bg-[#FAFAFA]">Cancel</button>
          <button
            onClick={submit}
            disabled={submitting || !bankAccountId}
            className="px-3 py-1.5 text-xs bg-[#2CA01C] text-white hover:bg-[#258A18] disabled:opacity-60"
            data-testid="recreate-deposit-submit"
          >
            {submitting ? "Creating…" : "Create deposit in QuickBooks"}
          </button>
        </div>
      </div>
    </div>
  );
}
